import type { Ref } from "vue";

type InfiniteScrollTermination =
  { total: Readonly<Ref<number>> } | { hasMore: Readonly<Ref<boolean>> };

/**
 * Builds a v-intersect handler for infinite-scroll lists/tables.
 * Supports two directive placements without extra configuration:
 * - bound on every v-for row via a numeric `:id` matching the row index —
 *   only fires loadMore when the intersecting row is the last one rendered.
 * - bound on a single sentinel element with no `id` after the list — fires
 *   loadMore on any intersection, since there's nothing else it could be.
 *
 * Termination accepts either a running `total` (stop once items.length
 * reaches it) or an explicit `hasMore` boolean, covering both call-site
 * shapes found across the codebase. `items`/`total`/`hasMore` accept plain
 * refs, computed refs, or storeToRefs output alike — only `.value` is read.
 */
export function useInfiniteScroll<T>(
  items: Readonly<Ref<T[] | null | undefined>>,
  termination: InfiniteScrollTermination,
  loadMore: () => unknown
) {
  let isLoadingMore = false;

  const hasMoreToLoad = () =>
    "total" in termination
      ? (items.value ?? []).length < termination.total.value
      : termination.hasMore.value;

  const onIntersect = (isIntersecting: boolean, entries: IntersectionObserverEntry[] = []) => {
    if (!isIntersecting || isLoadingMore) return;

    const targetId = entries[0]?.target?.id;
    const isLastRenderedRow = !targetId || Number(targetId) === (items.value ?? []).length - 1;
    if (!isLastRenderedRow || !hasMoreToLoad()) return;

    // loadMore's guard state (items/total/hasMore) only updates once its promise
    // resolves, so without this flag a second intersection firing before then
    // (rapid scroll) would trigger an overlapping, duplicate fetch of the same page.
    isLoadingMore = true;
    try {
      Promise.resolve(loadMore()).finally(() => {
        isLoadingMore = false;
      });
    } catch (error) {
      isLoadingMore = false;
      throw error;
    }
  };

  return { onIntersect };
}
